import SubTitle from '../Common/SubTitle/SubTitle';
import * as React from 'react';
import * as queryString from 'query-string';
import FormDialog from '../Common/Form/FormDialog/FormDialog';
import { Field, FormProps, reduxForm, SubmissionError } from 'redux-form';
import FormDialogFooter from '../Common/Form/FormDialog/FormDialogFooter';
import PrimaryButton from '../Common/Buttons/PrimaryButton';
import MessageBar from '../Common/MessageBar/MessageBar';
import AppLayoutContextUpdater from '../Common/AppLayoutContextUpdater/AppLayoutContextUpdater';
import * as formUtils from 'login-utils/formUtils';
import InputMap from '../Common/Inputs/InputMap';
import { getMFAMethod, resendToken, verifyMFAToken } from 'login-apis/MFAAPI';
import './MFAValidate.scss';
import { setHref, WindowWrapper } from 'login-utils/globalWrappers';
import { hashHistory } from 'react-router';

const inputs: FormInput[] = [
  {
    ...InputMap.SimpleTextInput,
    name: 'token',
    identifier: 'Enter Code',
  },
];

const FORM_NAME = 'MFAValidate';
const FORM_ERROR_ID = `${FORM_NAME}-formError`;

interface QueryProps {
  mfaId: string;
}
interface MFAFormProps extends ReactRouter.RouteComponentProps<{}, {}> {}
interface MFAFormProps extends FormProps<any, any, any> {
  goBackOnReSend?: boolean;
}
interface MFAFormState {
  messageBarText: string | null;
  messageType: MessageType;
  deliveryMethod: string;
  last4PhoneNumber: string;
}
interface FormData {
  token: string;
  rememberUser: boolean;
}

export class MFAValidate extends React.Component<MFAFormProps, MFAFormState> {
  constructor(props) {
    super(props);
    this.state = {
      messageBarText: null,
      messageType: 'error',
      deliveryMethod: '',
      last4PhoneNumber: '',
    };
  }

  getMFAId = () => {
    const { location } = this.props;
    // We are doing this because react router doesn't allow us to type the query props.
    return (queryString.parse(location.search) as QueryProps).mfaId;
  };

  async getMFAMethod() {
    const showMFALoadingError = () => {
      this.setState({
        messageType: 'error',
        messageBarText: 'Something went wrong, please try refresh the page.',
      });
    };

    try {
      const res = await getMFAMethod(this.getMFAId());
      if (res && res.data) {
        this.setState({
          deliveryMethod: res.data.deliveryMethod,
          last4PhoneNumber: res.data.last4PhoneNumber,
        });
      } else {
        throw new Error();
      }
    } catch (e) {
      showMFALoadingError();
    }
  }

  componentDidMount() {
    this.getMFAMethod();
  }

  submitForm = async (formData: FormData) => {
    const body = {
      token: formData.token,
      rememberUser: formData.rememberUser || false,
    };
    function showSubmitErrorMessage(response) {
      const unknownError = 'Something went wrong when verifying your token.';
      throw new SubmissionError<FormData>({ token: (response.data && response.data.message) || unknownError });
    }
    try {
      const res = await verifyMFAToken(this.getMFAId(), body);
      if (res.data && res.data.success) {
        // redirect to pipeline page after login.
        setHref('/');
      } else {
        showSubmitErrorMessage(res);
      }
    } catch (e) {
      showSubmitErrorMessage(e.response || {});
    }
  };

  resendCode = async e => {
    const { goBackOnReSend } = this.props;
    e.preventDefault();
    if (goBackOnReSend) {
      hashHistory.goBack();
      return;
    }
    const showResendErrorMessage = response => {
      const unknownError = 'Something went wrong when sending a new token.';
      this.setState({
        messageBarText: (response.data && response.data.message) || unknownError,
        messageType: 'error',
      });
    };
    try {
      const res = await resendToken(this.getMFAId());
      if (res.status === 200) {
        this.setState({
          messageBarText: 'Code sent',
          messageType: 'success',
        });
      } else {
        showResendErrorMessage(res);
      }
    } catch (e) {
      showResendErrorMessage(e.response || {});
    }
  };

  render() {
    const appTitle = '2-Step Verification';
    const { handleSubmit, submitting } = this.props;
    const { deliveryMethod, last4PhoneNumber } = this.state;
    const formatMap = {
      sms: 'text message',
      call: 'phone call',
    };
    const formattedDeliveryMethod = formatMap[deliveryMethod];
    const { messageType, messageBarText } = this.state;
    return (
      <div className="MFAValidate" id="it-MFAValidate">
        <AppLayoutContextUpdater appTitle={appTitle} footerConfig={null} appDocumentTitle={appTitle}>
          <div className="MFA-dialog" id="it-MFA-dialog">
            <form id="it-MFAValidateForm" onSubmit={handleSubmit!(this.submitForm)}>
              <FormDialog>
                <div className="MFAValidate-dialogBody">
                  {messageBarText && (
                    <MessageBar messageType={messageType} messageText={messageBarText} id="it-MFAValidate-MessageBar" />
                  )}
                  <SubTitle>
                    A <strong>{formattedDeliveryMethod}</strong> with your code has been sent to the phone number ending
                    with <strong>{last4PhoneNumber}</strong>.
                  </SubTitle>
                  <fieldset className="MFA-fieldsWrapper">
                    {inputs.map(input => <Field key={input.name} {...input} />)}
                  </fieldset>
                  <div className="MFAValidate-inputLabelWrapper BrandLinks">
                    Didn't get it?&nbsp;
                    <a id="it-MFAValidate-resendButton" href="#" onClick={this.resendCode}>
                      <strong>Resend Code</strong>
                    </a>
                  </div>
                </div>
                <FormDialogFooter>
                  <PrimaryButton buttonId="it-MFAValidate-submitButton" disabled={submitting} text="Verify" />
                  <Field
                    id="rememberUser"
                    className="MFAValidate-rememberUser"
                    name="rememberUser"
                    type="checkbox"
                    component="input"
                  />
                  <label htmlFor="rememberUser">Remember me for 30 days</label>
                </FormDialogFooter>
              </FormDialog>
            </form>
            {WindowWrapper.E2E_TEST && (
              <button id="it-MFAValidate-resendButtonAPI" onClick={() => resendToken(this.getMFAId())} />
            )}
          </div>
        </AppLayoutContextUpdater>
      </div>
    );
  }
}

function tokenValidation(values) {
  const errors: any = {};
  // check if all numbers
  if (!values.token) {
    errors.token = 'Please enter the token that was sent to you.';
  } else if (!values.token.match(/^\d+$/)) {
    // check if all numbers
    errors.token = 'Your token should only contain numbers.';
  }
  return errors;
}

const MFAValidateForm = reduxForm({
  form: FORM_NAME,
  validate: values => tokenValidation(values),
  onSubmitFail: syncErrors => formUtils.focusOnSubmitFail(syncErrors, FORM_ERROR_ID),
})(MFAValidate);

export default MFAValidateForm;
